import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertyDetails } from '../../../../core/models/property-details.model';

type NearbyPlace = PropertyDetails['nearby_places'][number];

@Component({
  selector: 'app-nearby-places',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="surface-card p-6 md:p-8 border border-border shadow-sm antialiased"
    >
      <div class="flex items-center gap-2 mb-6 pb-2 border-b border-border-subtle">
        <div class="w-1.5 h-3.5 bg-primary rounded-sm"></div>
        <h2 class="text-sm font-black text-text-primary uppercase tracking-wider font-mono">
          الموقع والأماكن المجاورة
        </h2>
      </div>

      @if (places && places.length) {
        <div class="space-y-2.5">
          @for (place of places; track place.name + place.type + place.distance_km) {
            <div
              class="group flex items-center justify-between p-3.5 bg-bg-secondary/50 hover:bg-surface-hover border border-border-subtle hover:border-primary/30 rounded-2xl transition-all duration-300"
            >
              <div class="flex items-center gap-3.5">
                <div
                  class="w-10 h-10 bg-surface-elevated border border-border rounded-xl flex items-center justify-center text-text-muted group-hover:text-primary group-hover:bg-primary/5 group-hover:border-primary/40 shadow-sm shrink-0 transition-all duration-300"
                >
                  <i [class]="getTypeIcon(place.type)" class="text-sm"></i>
                </div>
                <div class="space-y-0.5">
                  <div
                    class="text-xs md:text-sm font-bold text-text-primary group-hover:text-primary transition-colors tracking-tight"
                  >
                    {{ valueOrDefault(place.name, 'موقع استراتيجي') }}
                  </div>
                  <div
                    class="text-[10px] font-mono font-bold text-text-muted uppercase tracking-wider"
                  >
                    {{ valueOrDefault(place.type, 'وجهة') }}
                  </div>
                </div>
              </div>

              <div class="text-right space-y-1">
                <div class="text-xs font-mono font-black text-text-primary tracking-tight">
                  {{ place.travel_time || formatDistance(place.distance_km) }}
                </div>

                <div
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-elevated group-hover:bg-primary/10 rounded-md transition-colors"
                >
                  <i
                    [class]="place.travel_time ? 'fa-solid fa-car-side' : 'fa-solid fa-route'"
                    class="text-[9px] text-text-muted group-hover:text-primary transition-colors"
                  ></i>
                  <span
                    class="text-[10px] font-mono font-bold text-text-secondary group-hover:text-primary uppercase tracking-wide transition-colors"
                  >
                    {{ place.travel_time ? 'وقت القيادة' : 'مسافة' }}
                  </span>
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <div
          class="flex flex-col items-center justify-center text-center p-8 bg-bg-secondary/50 border border-border-subtle rounded-2xl"
        >
          <div
            class="w-12 h-12 bg-surface-elevated border border-border shadow-sm rounded-xl flex items-center justify-center text-text-muted mb-4"
          >
            <i class="fa-solid fa-radar fa-spin-pulse text-lg"></i>
          </div>
          <p class="text-xs font-semibold text-text-secondary tracking-tight">
            يتم حاليًا تعيين بيانات الأماكن المجاورة لهذه المنطقة.
          </p>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class NearbyPlacesComponent {
  @Input() places: NearbyPlace[] = [];

  valueOrDefault(value: unknown, fallback: string = 'غير متوفر'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text ? text : fallback;
  }

  formatDistance(distance: unknown): string {
    if (typeof distance === 'number' && distance > 0) {
      return `${distance} كم`;
    }
    return 'غير متوفر';
  }

  /**
   * دالة مطورة لتحليل نوع المعلم المحيط وإرجاع الأيقونة المعمارية المطابقة له
   */
  getTypeIcon(type: string): string {
    const normalized = (type || '').toLowerCase();

    if (
      normalized.includes('school') ||
      normalized.includes('university') ||
      normalized.includes('مدرسة') ||
      normalized.includes('جامعة')
    ) {
      return 'fa-solid fa-graduation-cap';
    }
    if (
      normalized.includes('hospital') ||
      normalized.includes('clinic') ||
      normalized.includes('medical') ||
      normalized.includes('مستشفى') ||
      normalized.includes('عيادة')
    ) {
      return 'fa-solid fa-house-medical';
    }
    if (
      normalized.includes('park') ||
      normalized.includes('nature') ||
      normalized.includes('garden') ||
      normalized.includes('حديقة')
    ) {
      return 'fa-solid fa-tree';
    }
    if (
      normalized.includes('metro') ||
      normalized.includes('station') ||
      normalized.includes('airport') ||
      normalized.includes('محطة')
    ) {
      return 'fa-solid fa-train-subway';
    }
    if (
      normalized.includes('mall') ||
      normalized.includes('shop') ||
      normalized.includes('market') ||
      normalized.includes('سوق') ||
      normalized.includes('مول')
    ) {
      return 'fa-solid fa-bag-shopping';
    }
    if (
      normalized.includes('museum') ||
      normalized.includes('landmark') ||
      normalized.includes('متحف') ||
      normalized.includes('معلم')
    ) {
      return 'fa-solid fa-monument';
    }
    if (
      normalized.includes('historic') ||
      normalized.includes('تاريخي') ||
      normalized.includes('قلعة')
    ) {
      return 'fa-solid fa-landmark';
    }
    if (
      normalized.includes('tower') ||
      normalized.includes('building') ||
      normalized.includes('برج')
    ) {
      return 'fa-solid fa-building';
    }

    return 'fa-solid fa-location-crosshairs';
  }
}
